import React, { useMemo } from "react"
import { useTranslation } from "react-i18next"

import { Action } from "shared/types/Action"
import { Readonly } from "shared/ui/readonly/Readonly"
import { TimeDifference } from "shared/ui/time-difference/TimeDifference"

import { ProjectFormData } from "features/project-management/types/ProjectFormData"
import { ProjectFormTabType } from "features/project-management/types/ProjectFormTabType"
import { ProjectModel } from "features/project-management/types/ProjectModel"
import { ProjectPageSidebarActions } from "pages/project-page/ui/ProjectPageSidebarActions"
import { ProjectForm } from "widgets/project/project-form/ProjectForm"

import "../../styles/_project-page-sidebar.scss"

export interface ProjectPageProjectSidebarViewModel {
  id: string
  formID: string
  model: ProjectModel
  isReadonly: boolean
  isDisabled?: boolean
  hideControls?: boolean

  onSubmit: (formData: ProjectFormData) => void

  actions: Action[]

  sidebarActiveTab: string | null
  setSidebarActiveTab: (tab: string | null) => void
}

const ProjectSidebarHeader = React.memo(
  ({ model }: { model: ProjectModel }) => {
    const { t } = useTranslation()

    const isLoading = model.core.data.project?.isLoading
    const lastChangedByUser = model.core.data.project?.data?.lastChangedBy

    return (
      <div className="project-page-sidebar__header">
        <div className="project-page-sidebar__title">
          <ProjectForm.Title />
        </div>
        <div className="project-page-sidebar__header-meta">
          <Readonly.Root
            className="project-card__last-changed"
            isLoading={model.core.data.project?.isLoading}
          >
            <Readonly.Label>{t("General.LastChanged")}</Readonly.Label>
            <Readonly.Value>
              <TimeDifference
                timeStamp={model.core.data.project?.data?.lastChanged}
              />
            </Readonly.Value>
          </Readonly.Root>
          {lastChangedByUser || isLoading ? (
            <Readonly.Root isLoading={model.core.data.project?.isLoading}>
              <Readonly.Label>{t("General.LastChangedBy")}</Readonly.Label>
              <Readonly.Value>
                {lastChangedByUser?.name || lastChangedByUser?.email}
              </Readonly.Value>
            </Readonly.Root>
          ) : null}
        </div>
      </div>
    )
  }
)

export const ProjectPageProjectSidebarView = React.memo(
  ({
    formID,
    model,
    isReadonly,
    isDisabled,
    hideControls,
    onSubmit,
    actions,
    sidebarActiveTab,
    setSidebarActiveTab
  }: ProjectPageProjectSidebarViewModel) => {
    const defaultFormData = useMemo(
      () => ({
        project: model.core.data.project?.data ?? undefined,
        clarificationRequest: {
          content:
            model.clarificationRequest?.data.clarificationRequest?.data
              ?.content ?? null
        }
      }),
      [
        model.core.data.project?.data,
        model.clarificationRequest?.data.clarificationRequest?.data?.content
      ]
    )
    return (
      <div
        className="project-page-sidebar project-page-sidebar--project"
        data-testid="project-sidebar"
      >
        <div className="project-page-sidebar__content">
          <ProjectForm
            id={formID}
            defaultFormData={defaultFormData}
            isReadonly={isReadonly}
            isDisabled={isDisabled}
            isLoading={model.core.data.project?.isLoading}
            onSubmit={onSubmit}
            model={model}
          >
            <ProjectSidebarHeader model={model} />
            <ProjectForm.Tabs
              activeTab={
                (sidebarActiveTab as ProjectFormTabType | null) ?? undefined
              }
              onTabChange={setSidebarActiveTab}
            />
          </ProjectForm>
        </div>
        {hideControls ? null : (
          <ProjectPageSidebarActions
            actions={actions}
            isIconOnly={isReadonly}
          />
        )}
      </div>
    )
  }
)
